import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  BottomNavigation, 
  BottomNavigationAction, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Add as AddIcon,
  LocalShipping as TransportationIcon,
  ReportProblem as UnusualIncidentIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AppDateLocalization from './AppDateLocalization';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { provider, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = () => {
    switch (location.pathname) {
      case '/clients':
        return 0;
      case '/visits':
        return 1;
      case '/transportation':
        return 2;
      case '/unusual-incidents':
        return 3;
      case '/new-entry':
      case '/new-visit':
        return 4;
      case '/profile':
        return 5;
      default:
        return 4; // Default to new entry
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#BFD3EA',
      }}
    >
      <AppBar position="static" sx={{ backgroundColor: '#2D4B73' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src="/logo-bar.png" 
              alt="EasyIP Logo" 
              style={{ height: '70px', maxWidth: '100%', objectFit: 'contain' }} 
            />
          </Box>
          {provider && (
            <Typography variant="body2" sx={{ mr: 2, color: 'white' }}>
              {provider.firstName} {provider.lastName}
            </Typography>
          )}
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, backgroundColor: '#BFD3EA' }}>
        <AppDateLocalization>{children}</AppDateLocalization>
      </Box>

      <Paper elevation={3} sx={{ flexShrink: 0, pb: 'env(safe-area-inset-bottom)' }}>
        <BottomNavigation
          value={getActiveTab()}
          showLabels
          onChange={(_, newValue) => {
            switch (newValue) {
              case 0:
                navigate('/clients');
                break;
              case 1:
                navigate('/visits');
                break;
              case 2:
                navigate('/transportation');
                break;
              case 3:
                navigate('/unusual-incidents');
                break;
              case 4:
                navigate('/new-entry');
                break;
              case 5:
                navigate('/profile');
                break;
            }
          }}
          sx={{
            height: 'auto',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              maxWidth: 'none',
              flex: '1 1 0',
              overflow: 'hidden',
              padding: '6px 2px 8px',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              fontWeight: 500,
              opacity: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            },
            '& .MuiBottomNavigationAction-label.Mui-selected': {
              fontSize: '0.75rem',
              fontWeight: 600,
              opacity: 1,
            },
          }}
        >
          <BottomNavigationAction 
            label="Clients" 
            icon={<PeopleIcon />} 
            sx={{
              '&.Mui-selected': {
                color: '#2D4B73',
              },
            }}
          />
          <BottomNavigationAction 
            label="Visit History" 
            icon={<EventIcon />} 
            sx={{
              '&.Mui-selected': {
                color: '#2D4B73',
              },
            }}
          />
          <BottomNavigationAction 
            label="Transportation" 
            icon={<TransportationIcon />} 
            sx={{
              '&.Mui-selected': {
                color: '#2D4B73',
              },
            }}
          />
          <BottomNavigationAction 
            label="UI" 
            icon={<UnusualIncidentIcon />} 
            sx={{
              '&.Mui-selected': {
                color: '#2D4B73',
              },
            }}
          />
          <BottomNavigationAction 
            label="New Entry" 
            icon={<AddIcon />} 
            sx={{
              '&.Mui-selected': {
                color: '#2D4B73',
              },
            }}
          />
          <BottomNavigationAction 
            label="Profile" 
            icon={<PersonIcon />} 
            sx={{
              '&.Mui-selected': {
                color: '#2D4B73',
              },
            }}
          />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default Layout;
